import { getCatalogIndex } from '../content/catalog-index'
import { foldGermanChars } from '../content/slug'
import { DEFAULT_LOCALE } from './i18n/config'
import type { Locale } from './i18n/config'
import { localizedPath } from './i18n/routes'

export type SearchResultItem = {
  href: string
  title: string
  description: string
  /** Pro course / lesson: rendered with a "Pro" badge. */
  pro?: boolean
}

/**
 * Locale-agnostic group keys. The visible label is resolved at render time via
 * the dictionary (`search.groups.<key>`), so search results render German group
 * headings under `/de` and English ones at the root.
 */
export type SearchResultGroupKey =
  | 'lessons'
  | 'courses'
  | 'resources'
  | 'knowledge'
  | 'fundamentals'
  | 'builds'
  | 'glossary'
  | 'guides'
  | 'compare'
  | 'tools'
  | 'changelog'
  | 'community'

export type SearchResultGroup = {
  group: SearchResultGroupKey
  items: SearchResultItem[]
}

/**
 * Build the full search index for a locale. Titles and descriptions are read
 * from the slim, body-free catalog index ({@link ../content/catalog-index.ts})
 * - search only matches titles + short descriptions, so it never needs (and
 * must not pull onto the critical path) the full lesson/article/build bodies.
 * Every href is derived from the shared route map ({@link ./i18n/routes.ts}) so
 * results link to the correct localized URL (e.g. `/courses/...` in EN,
 * `/de/kurse/...` in DE) instead of a hardcoded path that would 301-bounce.
 */
function buildSearchSource(locale: Locale): SearchResultGroup[] {
  const {
    articles,
    builds,
    changelogEntries,
    communityPosts,
    comparisons,
    courses,
    fundamentals,
    glossaryTerms,
    guides,
    lessons,
    resources,
    tools,
  } = getCatalogIndex(locale)
  return [
    {
      group: 'lessons',
      items: lessons.map((lesson) => ({
        href: localizedPath('lesson', locale, {
          courseSlug: lesson.courseSlug,
          lessonSlug: lesson.slug,
        }),
        title: lesson.title,
        description: lesson.summary,
        pro: lesson.access === 'pro',
      })),
    },
    {
      group: 'courses',
      items: courses.map((course) => ({
        href: localizedPath('course', locale, { courseSlug: course.slug }),
        title: course.title,
        description: course.description,
        pro: course.tier === 'pro',
      })),
    },
    {
      group: 'resources',
      items: resources.map((resource) => ({
        href: localizedPath('resource', locale, {
          resourceSlug: resource.slug,
        }),
        title: resource.title,
        description: resource.shortDescription,
      })),
    },
    {
      group: 'knowledge',
      items: articles.map((article) => ({
        href: localizedPath('article', locale, { articleSlug: article.slug }),
        title: article.title,
        description: article.description,
      })),
    },
    {
      group: 'fundamentals',
      items: fundamentals.map((fundamental) => ({
        href: localizedPath('fundamental', locale, {
          fundamentalSlug: fundamental.slug,
        }),
        title: fundamental.title,
        description: fundamental.description,
      })),
    },
    {
      group: 'builds',
      items: builds.map((build) => ({
        href: localizedPath('build', locale, { buildSlug: build.slug }),
        title: build.title,
        description: build.tagline,
      })),
    },
    {
      group: 'glossary',
      items: glossaryTerms.map((term) => ({
        href: localizedPath('glossaryTerm', locale, { termSlug: term.slug }),
        title: term.title,
        description: term.description,
      })),
    },
    {
      group: 'guides',
      items: guides.map((guide) => ({
        href: localizedPath('guide', locale, { guideSlug: guide.slug }),
        title: guide.title,
        description: guide.description,
      })),
    },
    {
      group: 'compare',
      items: comparisons.map((comparison) => ({
        href: localizedPath('comparison', locale, {
          comparisonSlug: comparison.slug,
        }),
        title: comparison.title,
        description: comparison.description,
      })),
    },
    {
      group: 'tools',
      items: tools.map((tool) => ({
        href: localizedPath('tool', locale, { toolSlug: tool.slug }),
        title: tool.title,
        description: tool.description,
      })),
    },
    {
      group: 'changelog',
      items: changelogEntries.map((entry) => ({
        href: localizedPath('changelogEntry', locale, {
          entrySlug: entry.slug,
        }),
        title: entry.title,
        description: entry.excerpt,
      })),
    },
    {
      group: 'community',
      items: communityPosts.map((post) => ({
        href: localizedPath('communityPost', locale, { postSlug: post.slug }),
        title: post.title,
        description: post.excerpt,
      })),
    },
  ]
}

export function buildSearchResults(
  query: string,
  locale: Locale = DEFAULT_LOCALE,
): SearchResultGroup[] {
  const searchSource = buildSearchSource(locale)
  const normalizedQuery = normalizeSearchText(query)

  if (!normalizedQuery) {
    return searchSource.map((group) => ({
      ...group,
      items: group.items.slice(0, 4),
    }))
  }

  return searchSource
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) =>
          normalizeSearchText(`${item.title} ${item.description}`).includes(
            normalizedQuery,
          ),
        )
        .slice(0, 8),
    }))
    .filter((group) => group.items.length > 0)
}

/**
 * Normalise text for search comparison: trims, lowercases, transliterates
 * German umlauts/eszett and strips diacritics. Uses the SAME
 * {@link foldGermanChars} helper as `slugify`, so a German query matches German
 * copy exactly the way a German title slugifies (e.g. "Grundkurs für" and
 * "fuer" both fold to "fuer").
 */
export function normalizeSearchText(value: string): string {
  return foldGermanChars(value.trim())
}
